import {
  Component,
  EventEmitter,
  inject,
  Input,
  Output,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { isCurrentWeek, isToday, isYesterday } from '../../../../../helpers';
import {
  ATTACHMENTS_TYPES,
  CHAT_EXTERNAL_TYPE,
  CHAT_SUB_TYPES,
  entityIcons,
  MESSAGE_TYPES,
  SENDER_TYPES,
} from '../../../../../enum/constants';
import { Observable } from 'rxjs';
import { IChatItem } from '../../../../../interfaces';
import { CHAT_ITEM_SUBMENU, CHAT_TABS, IMenuChat } from '../chat-list/chat-list.interface';
import { ChatManager, ChatService } from '../../../../../services/chat.service';
import { BaseService } from '../../../../../services/base.service';
import { ConnectedPosition, Overlay, OverlayRef } from '@angular/cdk/overlay';
import { CdkContextMenuTrigger } from '@angular/cdk/menu';
import { TemplatePortal } from '@angular/cdk/portal';
import { CrmService } from '../../../../../services/crm.service';
import { ChatTag, TagService } from '../../../../../services/tag.service';

export enum ACTIONS {
  CLOSE = 'CLOSE',
  OPEN = 'OPEN',
  PIN = 'PIN',
  UNPIN = 'UNPIN',
  INCLUDE_IN_AUTOMATION = 'INCLUDE_IN_AUTOMATION',
  EXCLUDE_FROM_AUTOMATION = 'EXCLUDE_FROM_AUTOMATION',
  BLOCK = 'BLOCK',
  UNBLOCK = 'UNBLOCK',
  READ = 'READ',
  MARK_AS_UNREAD = 'MARK_AS_UNREAD',
  NOT_SPAM = 'NOT_SPAM',
  MOVE_TO_SPAM = 'MOVE_TO_SPAM',
  EXCLUDE_FROM_CARD = 'EXCLUDE_FROM_CARD',
  DELETE = 'DELETE',
  TAG_UPDATE = 'TAG_UPDATE',
  MANAGER_UPDATE = 'MANAGER_UPDATE',
}

export interface IChatAction {
  action: ACTIONS;
  chatId: string;
}

@Component({
  selector: 'app-chat-list-item',
  templateUrl: './chat-list-item.component.html',
  styleUrl: './chat-list-item.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ChatListItemComponent {
  protected readonly crmService = inject(CrmService);
  private readonly chatService = inject(ChatService);
  private readonly overlay = inject(Overlay);
  private readonly viewContainerRef = inject(ViewContainerRef);
  protected readonly baseService = inject(BaseService);
  private readonly tagService = inject(TagService);

  @ViewChild('menu') menuTemplate!: TemplateRef<any>;
  @ViewChild(CdkContextMenuTrigger) private contextMenuTrigger?: CdkContextMenuTrigger;
  @ViewChild('mobileSubmenu') mobileSubmenuTemplate!: TemplateRef<unknown>;
  private _chat!: IChatItem;
  private _cardId: string | null = null;
  @Input() selected!: boolean;
  @Output() onActionEmit = new EventEmitter<IChatAction>();
  private overlayRef!: OverlayRef;
  private mobileSubmenuOverlayRef?: OverlayRef;
  protected readonly entityIcons = entityIcons;
  protected chatMenus: IMenuChat[] = [];
  protected isAttachmentsType: boolean = false;
  protected activeSubmenu: CHAT_ITEM_SUBMENU | null = null;
  protected isMobileMenuOpen: boolean = false;
  protected mobileMenuOrigin!: HTMLElement;
  protected tags: ChatTag[] = [];
  protected tagSearchTerm = '';
  protected isTagsLoading: boolean = false;
  protected readonly updatingTagIds = new Set<string>();
  protected managers: ChatManager[] = [];
  protected managerSearchTerm = '';
  protected isManagersLoading: boolean = false;
  protected readonly mobileMenuPositions: ConnectedPosition[] = [
    { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetY: 4 },
    { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom', offsetY: -4 },
  ];

  @Input()
  set chat(value: IChatItem) {
    this._chat = value;
    this.initChatMenus();
    this.isAttachmentsType = Object.keys(ATTACHMENTS_TYPES).some((v) => v === value?.last_message?.type);
  }

  get chat(): IChatItem {
    return this._chat;
  }

  @Input()
  set cardId(value: string | null) {
    this._cardId = value;
    if (this._chat) this.initChatMenus();
  }

  get cardId(): string | null {
    return this._cardId;
  }

  private onActionClick(action: ACTIONS): void {
    const chatId = this.chat.id;

    switch (action) {
      case ACTIONS.CLOSE:
        this.chatService.onMoveChat(chatId, CHAT_TABS.CLOSED);
        break;
      case ACTIONS.OPEN:
        this.chatService.onMoveChat(chatId, CHAT_TABS.ACTIVE);
        break;
      case ACTIONS.NOT_SPAM:
        this.chatService.onMoveChat(chatId, CHAT_TABS.ACTIVE);
        break;
      case ACTIONS.MOVE_TO_SPAM:
        this.chatService.onMoveChat(chatId, CHAT_TABS.SPAM);
        break;
      case ACTIONS.PIN:
        this.runRequest(action, this.chatService.updateChat(chatId, { is_pinned: true }));
        return;
      case ACTIONS.UNPIN:
        this.runRequest(action, this.chatService.updateChat(chatId, { is_pinned: false }));
        return;
      case ACTIONS.EXCLUDE_FROM_AUTOMATION:
        this.runRequest(action, this.chatService.updateChat(chatId, { is_automation_excluded: true }));
        return;
      case ACTIONS.INCLUDE_IN_AUTOMATION:
        this.runRequest(action, this.chatService.updateChat(chatId, { is_automation_excluded: false }));
        return;
      case ACTIONS.READ:
        this.runRequest(action, this.chatService.readChat(this.chat));
        return;
      case ACTIONS.MARK_AS_UNREAD:
        this.runRequest(action, this.chatService.updateChat(chatId, { is_mark_unread: true }));
        return;
      case ACTIONS.BLOCK:
      case ACTIONS.UNBLOCK:
        this.onBlockUser();
        break;
      case ACTIONS.DELETE:
        this.runRequest(action, this.chatService.deleteChat(chatId));
        return;
    }

    this.emitAction(action, chatId);
  }

  private runRequest(action: ACTIONS, request: Observable<unknown>): void {
    const chatId = this.chat.id;

    request.subscribe({
      next: () => this.emitAction(action, chatId),
    });
  }

  private emitAction(action: ACTIONS, chatId: string): void {
    this.onActionEmit.emit({ action, chatId });
  }

  private initChatMenus() {
    this.chatMenus = [];

    if (this.chat.tab_status !== CHAT_TABS.SPAM) {
      if (this.chat.tab_status === CHAT_TABS.ACTIVE) {
        this.chatMenus.push({
          title: 'close',
          icon: 'archive',
          action: () => this.onActionClick(ACTIONS.CLOSE),
        });
      } else if (this.chat.tab_status === CHAT_TABS.CLOSED) {
        this.chatMenus.push({
          title: 'open',
          icon: 'unarchive',
          action: () => this.onActionClick(ACTIONS.OPEN),
        });
      }
    }

    if (this.chat.is_pinned) {
      this.chatMenus.push({
        title: 'unpin',
        icon: 'unpin',
        is_svg: true,
        action: () => this.onActionClick(ACTIONS.UNPIN),
      });
    } else {
      this.chatMenus.push({
        title: 'pin',
        icon: 'pin',
        is_svg: true,
        action: () => this.onActionClick(ACTIONS.PIN),
      });
    }

    if (this.chat.is_automation_excluded) {
      this.chatMenus.push({
        title: 'include_in_automation',
        icon: 'play_circle',
        action: () => this.onActionClick(ACTIONS.INCLUDE_IN_AUTOMATION),
      });
    } else {
      this.chatMenus.push({
        title: 'exclude_from_automation',
        icon: 'block',
        action: () => this.onActionClick(ACTIONS.EXCLUDE_FROM_AUTOMATION),
      }); 
    }

    this.chatMenus.push({
      title: 'chat_tags',
      icon: 'sell',
      submenu: 'tags',
      action: () => {},
    });

    this.chatMenus.push({
      title: 'manager',
      icon: 'person',
      submenu: 'managers',
      action: () => {},
    });

    if ([CHAT_EXTERNAL_TYPE.OLX, CHAT_EXTERNAL_TYPE.AUTO_RIA].includes(this.chat.external_type)) {
      if (this.chat.is_blocked) {
        this.chatMenus.push({
          title: 'unblock',
          icon: 'do_not_disturb_off',
          action: () => this.onActionClick(ACTIONS.UNBLOCK),
        }); 
      } else {
        this.chatMenus.push({
          title: 'block',
          icon: 'do_not_disturb_on',
          action: () => this.onActionClick(ACTIONS.BLOCK),
        }); 
      }
    }

    // TODO: Implements it next sprint
    // if (!this.chat.is_blocked) {
    //   this.chatMenus.push({ title: 'mute', icon: 'notifications_off', action: () => {} })
    // } else {
    //   this.chatMenus.push({ title: 'unmute', icon: 'notifications', action: () => {} })
    // }

    if (this.chat.is_mark_unread || this.chat.unread_count) {
      this.chatMenus.push({ title: 'read', icon: 'mark_chat_read', action: () => this.onActionClick(ACTIONS.READ) });
    } else {
      this.chatMenus.push({
        title: 'mark_as_unread',
        icon: 'mark_chat_unread',
        action: () => this.onActionClick(ACTIONS.MARK_AS_UNREAD),
      });
    }

    if (this.chat.tab_status === CHAT_TABS.SPAM) {
      this.chatMenus.push({
        title: 'not_spam',
        icon: 'move_to_inbox',
        action: () => this.onActionClick(ACTIONS.NOT_SPAM),
      });
    } else {
      this.chatMenus.push({
        title: 'move_to_spam',
        icon: 'report',
        action: () => this.onActionClick(ACTIONS.MOVE_TO_SPAM),
      });
    }

    if (this._cardId) {
      this.chatMenus.push({
        title: 'exclude_from_card',
        icon: 'link_off',
        action: () => this.onActionClick(ACTIONS.EXCLUDE_FROM_CARD),
      });
    }

    if (this.baseService.user.is_admin) {
      this.chatMenus.push({
        title: 'delete',
        icon: 'delete',
        is_lines: true,
        action: () => this.onActionClick(ACTIONS.DELETE),
      });
    }
  }

  private onBlockUser() {
    const user_id = String(this.chat.x_data?.interlocutor_id || this.chat.x_data?.opponent?.id || '');
    if (!user_id) return;

    if (this.chat.is_blocked) {
      this.chatService.unblockUser(user_id, this.chat.external_type);
    } else {
      this.chatService.blockUser(user_id, this.chat.external_type, this.chat.title).subscribe();
    }
  }

  protected openSubmenu(submenu: CHAT_ITEM_SUBMENU, event: MouseEvent): void {
    event.stopPropagation();
    this.tagSearchTerm = '';
    this.managerSearchTerm = '';
    this.activeSubmenu = submenu;
    this.loadSubmenuData(submenu);

    if (this.baseService.isMobile) {
      this.isMobileMenuOpen = false;
      this.openMobileSubmenu();
    }
  }

  protected closeMobileSubmenu(event: MouseEvent): void {
    event.stopPropagation();
    this.activeSubmenu = null;
    this.mobileSubmenuOverlayRef?.dispose();
    this.mobileSubmenuOverlayRef = undefined;
  }

  private openMobileSubmenu(): void {
    if (this.mobileSubmenuOverlayRef) return;

    this.mobileSubmenuOverlayRef = this.overlay.create({
      positionStrategy: this.overlay
        .position()
        .flexibleConnectedTo(this.mobileMenuOrigin)
        .withPositions(this.mobileMenuPositions)
        .withPush(true)
        .withViewportMargin(8),
      panelClass: ['w-80', 'max-w-[calc(100vw-2rem)]'],
      hasBackdrop: true,
      backdropClass: 'cdk-overlay-transparent-backdrop',
    });

    this.mobileSubmenuOverlayRef.backdropClick().subscribe(() => this.closeMobileSubmenu(new MouseEvent('click')));
    this.mobileSubmenuOverlayRef.attach(new TemplatePortal(this.mobileSubmenuTemplate, this.viewContainerRef));
  }

  private loadSubmenuData(submenu: CHAT_ITEM_SUBMENU): void {
    if (submenu === 'tags') this.loadTags();
    else this.loadManagers();
  }

  private loadTags(): void {
    if (this.tags.length || this.isTagsLoading) return;

    this.isTagsLoading = true;
    this.tagService.getTags().subscribe({
      next: (tags) => {
        this.tags = tags;
        this.isTagsLoading = false;
      },
      error: () => (this.isTagsLoading = false),
    });
  }

  private loadManagers(): void {
    if (this.isManagersLoading) return;

    this.isManagersLoading = true;
    this.managers = [];
    const chatId = this.chat.id;
    this.chatService.getAssignableManagers(chatId).subscribe((managers) => {
      if (this.chat.id === chatId) this.managers = managers;
      this.isManagersLoading = false;
    });
  }

  protected closeSubmenu(event: MouseEvent): void {
    event.stopPropagation();
    this.activeSubmenu = null;
  }

  /**
   * CDK closes the menu when a mobile tap moves focus away from a menu item.
   * These controls change the current menu view, so they must not take focus.
   */
  protected keepMenuFocus(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
  }

  protected resetSubmenu(): void {
    this.activeSubmenu = null;
  }

  protected toggleMobileMenu(event: MouseEvent): void {
    event.stopPropagation();
    const target = event.target as HTMLElement;
    this.mobileMenuOrigin = target.closest('button') || target;
    this.activeSubmenu = null;
    this.isMobileMenuOpen = !this.isMobileMenuOpen;
  }

  protected closeMobileMenu(): void {
    this.isMobileMenuOpen = false;
    // Opening a mobile submenu closes this overlay too; keep the submenu that is being shown
    if (!this.mobileSubmenuOverlayRef) this.activeSubmenu = null;
  }

  protected runMobileMenuAction(action: () => void): void {
    this.closeMobileMenu();
    action();
  }

  private closeMenu(): void {
    this.activeSubmenu = null;
    this.isMobileMenuOpen = false;
    this.contextMenuTrigger?.close();
    this.mobileSubmenuOverlayRef?.dispose();
    this.mobileSubmenuOverlayRef = undefined;
  }

  protected isTagAssigned(tagId: string): boolean {
    return !!this.chat.tags?.some((tag) => tag.id === tagId);
  }

  protected isTagUpdating(tagId: string): boolean {
    return this.updatingTagIds.has(tagId);
  }

  protected get filteredTags(): ChatTag[] {
    const searchTerm = this.tagSearchTerm.trim().toLocaleLowerCase();
    if (!searchTerm) return this.tags;

    return this.tags.filter((tag) => tag.name.toLocaleLowerCase().includes(searchTerm));
  }

  protected toggleTag(tagId: string, event: MouseEvent): void {
    event.stopPropagation();
    if (this.updatingTagIds.size) return;
    this.closeMenu();
    const selectedTagIds = this.chat.tags?.map((tag) => tag.id) || [];
    const nextTagIds = this.isTagAssigned(tagId)
      ? selectedTagIds.filter((id) => id !== tagId)
      : [...selectedTagIds, tagId];

    this.updatingTagIds.add(tagId);
    this.chatService.updateChatTags(this.chat.id, nextTagIds).subscribe({
      next: () => {
        this.updatingTagIds.delete(tagId);
        this.emitAction(ACTIONS.TAG_UPDATE, this.chat.id);
      },
      error: () => this.updatingTagIds.delete(tagId),
    });
  }

  protected isManagerAssigned(managerId: string | null): boolean {
    return (this.chat.assigned_user?.id ?? null) === managerId;
  }

  protected get filteredManagers(): ChatManager[] {
    const searchTerm = this.managerSearchTerm.trim().toLocaleLowerCase();
    if (!searchTerm) return this.managers;

    return this.managers.filter((manager) => manager.name.toLocaleLowerCase().includes(searchTerm));
  }

  protected changeManager(managerId: string | null, event: MouseEvent): void {
    event.stopPropagation();
    this.closeMenu();
    if (this.isManagerAssigned(managerId)) return;

    const chatId = this.chat.id;
    this.chatService.updateChat(chatId, { assigned_user_id: managerId }).subscribe({
      next: () => this.emitAction(ACTIONS.MANAGER_UPDATE, chatId),
      // The chat may become inaccessible after reassigning it to another manager (chat distribution)
      error: () => this.chatService.reloadChatList(),
    });
  }

  // protected openMenu(event: TouchEvent): void {
  //   event.stopPropagation();
  //   console.log("start");
  //   if (!this.overlayRef) {
  //     this.overlayRef = this.overlay.create({
  //       positionStrategy: this.overlay
  //         .position()
  //         .flexibleConnectedTo({ x: event.touches[0].clientX, y: event.touches[0].clientY })
  //         .withPositions([
  //           { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'top' }
  //         ]),
  //       hasBackdrop: true,
  //       backdropClass: 'cdk-overlay-transparent-backdrop'
  //     });
  //
  //
  //     this.overlayRef.backdropClick().subscribe(() => this.overlayRef.dispose());
  //     document.addEventListener('click', this.closeMenuOnOutsideClick.bind(this), true);
  //
  //   }
  //
  //   this.overlayRef.attach(new TemplatePortal(this.menuTemplate, this.viewContainerRef));
  // }
  // closeMenuOnOutsideClick(event: MouseEvent): void {
  //   console.log("start");
  //
  //   const targetElement = event.target as HTMLElement;
  //
  //   if (!this.overlayRef.overlayElement.contains(targetElement)) {
  //     this.overlayRef.dispose();
  //     this.overlayRef = null!;
  //     document.removeEventListener('click', this.closeMenuOnOutsideClick.bind(this), true);
  //   }
  // }

  protected readonly isToday = isToday;
  protected readonly isCurrentWeek = isCurrentWeek;
  protected readonly CHAT_EXTERNAL_TYPE = CHAT_EXTERNAL_TYPE;
  protected readonly isYesterday = isYesterday;
  protected readonly Array = Array;
  protected readonly Object = Object;
  protected readonly SENDER_TYPES = SENDER_TYPES;
  protected readonly CHAT_SUB_TYPES = CHAT_SUB_TYPES;
  protected readonly MESSAGE_TYPES = MESSAGE_TYPES;
}
